<template>
  <v-snackbar
    :model-value="activeNotification !== null"
    :timeout="timeout"
    :color="snackbarColor"
    :location="snackbarLocation"
    @update:modelValue="onVisibilityChange"
  >
    <div :data-test-id="activeNotification?.['data-test-id']">
      <vue-fontawesome
        v-if="shouldRenderIcon"
        :icon="notificationIcon"
        class="notification-icon"
      />
      {{ activeNotification?.message }}
    </div>
    <template #actions>
      <v-btn
        v-if="activeNotification?.closeable !== false"
        variant="text"
        @click="dismiss"
      >
        {{ t('Close') }}
      </v-btn>
    </template>
  </v-snackbar>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'

import {
  useNotificationsStore,
  type NotificationPosition,
} from '@/stores/notifications'

const { t } = useI18n()
const notificationsStore = useNotificationsStore()
const { activeNotification } = storeToRefs(notificationsStore)

type SnackbarLocation =
  | 'top'
  | 'bottom'
  | 'top right'
  | 'top left'
  | 'bottom right'
  | 'bottom left'

const locationMap: Record<NotificationPosition, SnackbarLocation> = {
  top: 'top',
  bottom: 'bottom',
  'is-top': 'top',
  'is-bottom': 'bottom',
  'is-top-right': 'top right',
  'is-top-left': 'top left',
  'is-bottom-right': 'bottom right',
  'is-bottom-left': 'bottom left',
}

const iconMap = {
  info: ['fas', 'info-circle'],
  success: ['fas', 'check-circle'],
  warning: ['fas', 'exclamation-triangle'],
  error: ['fas', 'exclamation-circle'],
} as const

const snackbarColor = computed(() => activeNotification.value?.type ?? 'info')

const snackbarLocation = computed(
  () => locationMap[activeNotification.value?.position ?? 'is-top']
)

const shouldRenderIcon = computed(
  () => activeNotification.value?.hasIcon !== false
)

const notificationIcon = computed(
  () => iconMap[activeNotification.value?.type ?? 'info']
)

const timeout = computed(() => {
  if (!activeNotification.value) {
    return -1
  }

  if (activeNotification.value.autoClose) {
    return activeNotification.value.duration ?? 5000
  }

  return -1
})

const dismiss = () => {
  notificationsStore.dismissNotification()
}

const onVisibilityChange = (isVisible: boolean) => {
  if (!isVisible) {
    dismiss()
  }
}
</script>

<style scoped lang="scss">
.notification-icon {
  margin-inline-end: 0.5rem;
}
</style>
